import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  MessageSquare, Send, Plus, Search, User, Clock, CheckCircle2, Archive,
  Mail, Phone, Building2, Stethoscope, RefreshCw, Loader2, ChevronRight,
  AlertCircle, Filter, Star, Paperclip, MoreVertical, ArrowLeft, Shield
} from "lucide-react";

const PRIORITY_CONFIG = {
  urgent: { label: "Urgent", color: "bg-red-500 text-white" },
  high: { label: "High", color: "bg-orange-500 text-white" },
  normal: { label: "Normal", color: "bg-blue-500 text-white" },
  low: { label: "Low", color: "bg-gray-500 text-white" }
};

const THREAD_STATUS = {
  active: { label: "Active", color: "bg-green-100 text-green-800" },
  archived: { label: "Archived", color: "bg-gray-100 text-gray-800" },
  closed: { label: "Closed", color: "bg-slate-100 text-slate-800" }
};

export default function PhysicianMessagingCenter() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("inbox");
  
  // Threads & Messages
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Contacts
  const [contacts, setContacts] = useState([]);
  const [searchContacts, setSearchContacts] = useState("");
  
  // New Thread
  const [showNewThread, setShowNewThread] = useState(false);
  const [newThread, setNewThread] = useState({
    subject: "",
    message: "",
    recipient_physician_id: "",
    recipient_name: "",
    recipient_email: "",
    referral_id: "",
    patient_name: "",
    priority: "normal"
  });
  
  // New Contact
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({
    name: "",
    npi: "",
    specialty: "",
    organization: "",
    email: "",
    phone: "",
    fax: ""
  });
  
  // Reply
  const [replyMessage, setReplyMessage] = useState("");
  const [sending, setSending] = useState(false);

  const allowedRoles = ["admin", "administrator", "staff", "nurse", "physician", "provider", "coordinator"];

  useEffect(() => {
    if (authLoading) return;
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      toast.error("Access denied");
      navigate("/");
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadThreads(),
        loadContacts(),
        loadUnreadCount()
      ]);
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadThreads = async (status = "active") => {
    try {
      const response = await api.get(`/physician-messaging/threads?status=${status}`);
      setThreads(response.data.threads || []);
    } catch (error) {
      console.error("Failed to load threads:", error);
      setThreads([]);
    }
  };

  const loadContacts = async () => {
    try {
      const params = searchContacts ? `?search=${encodeURIComponent(searchContacts)}` : "";
      const response = await api.get(`/physician-messaging/contacts${params}`);
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error("Failed to load contacts:", error);
      setContacts([]);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await api.get("/physician-messaging/unread-count");
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to load unread count:", error);
    }
  };

  const loadThreadMessages = async (threadId) => {
    try {
      const response = await api.get(`/physician-messaging/threads/${threadId}`);
      setSelectedThread(response.data.thread);
      setMessages(response.data.messages || []);
      loadUnreadCount(); // Refresh unread count
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const handleCreateThread = async () => {
    if (!newThread.subject || !newThread.message) {
      toast.error("Subject and message are required");
      return;
    }
    if (!newThread.recipient_physician_id && !newThread.recipient_name) {
      toast.error("Please select or enter a recipient");
      return;
    }

    setSending(true);
    try {
      const response = await api.post("/physician-messaging/threads", newThread);
      toast.success("Message sent successfully");
      setShowNewThread(false);
      setNewThread({
        subject: "", message: "", recipient_physician_id: "", recipient_name: "",
        recipient_email: "", referral_id: "", patient_name: "", priority: "normal"
      });
      loadThreads();
      loadThreadMessages(response.data.thread_id);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedThread) return;

    setSending(true);
    try {
      await api.post(`/physician-messaging/threads/${selectedThread.id}/messages`, {
        thread_id: selectedThread.id,
        message: replyMessage
      });
      toast.success("Reply sent");
      setReplyMessage("");
      loadThreadMessages(selectedThread.id);
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleArchiveThread = async (threadId) => {
    try {
      await api.put(`/physician-messaging/threads/${threadId}/archive`);
      toast.success("Thread archived");
      setSelectedThread(null);
      setMessages([]);
      loadThreads();
    } catch (error) {
      toast.error("Failed to archive thread");
    }
  };

  const handleAddContact = async () => {
    if (!newContact.name) {
      toast.error("Physician name is required");
      return;
    }

    try {
      await api.post("/physician-messaging/contacts", newContact);
      toast.success("Contact added");
      setShowNewContact(false);
      setNewContact({ name: "", npi: "", specialty: "", organization: "", email: "", phone: "", fax: "" });
      loadContacts();
    } catch (error) {
      toast.error("Failed to add contact");
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="messaging-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-400 mb-4" />
          <p className="text-slate-400">Loading Secure Messaging...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="physician-messaging-center">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Shield className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                  Secure Physician Messaging
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500 ml-2">{unreadCount} unread</Badge>
                  )}
                </h1>
                <p className="text-indigo-100">HIPAA-compliant encrypted communication with referring physicians</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={loadData} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <RefreshCw className="h-4 w-4 mr-2" />Refresh
              </Button>
              <Button onClick={() => setShowNewThread(true)} className="bg-white text-indigo-600 hover:bg-indigo-50" data-testid="new-message-btn">
                <Plus className="h-4 w-4 mr-2" />New Message
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Thread List */}
          <Card className="bg-slate-800 border-slate-700 lg:col-span-1">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg">Message Threads</CardTitle>
                <Button size="sm" variant="outline" className="border-slate-600" onClick={() => setShowNewContact(true)}>
                  <User className="h-4 w-4 mr-1" />Add Contact
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); loadThreads(v === "archived" ? "archived" : "active"); }}>
                <TabsList className="w-full bg-slate-700/50 rounded-none border-b border-slate-700">
                  <TabsTrigger value="inbox" className="flex-1 data-[state=active]:bg-indigo-600">
                    <Mail className="h-4 w-4 mr-2" />Inbox
                  </TabsTrigger>
                  <TabsTrigger value="archived" className="flex-1 data-[state=active]:bg-indigo-600">
                    <Archive className="h-4 w-4 mr-2" />Archived
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="inbox" className="m-0">
                  <ScrollArea className="h-[500px]">
                    {threads.length === 0 ? (
                      <div className="text-center py-12 text-slate-400">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No messages yet</p>
                        <Button size="sm" className="mt-3 bg-indigo-600" onClick={() => setShowNewThread(true)}>
                          Start a Conversation
                        </Button>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-700">
                        {threads.map((thread) => (
                          <div
                            key={thread.id}
                            className={`p-4 cursor-pointer transition-colors ${
                              selectedThread?.id === thread.id ? "bg-indigo-600/20" : "hover:bg-slate-700/50"
                            }`}
                            onClick={() => loadThreadMessages(thread.id)}
                            data-testid={`thread-${thread.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <Avatar className="h-10 w-10 bg-indigo-500/20">
                                <AvatarFallback className="text-indigo-300 text-sm">
                                  {getInitials(thread.participants?.find(p => p.type === "external")?.name || thread.subject)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium text-white truncate">
                                    {thread.participants?.find(p => p.type === "external")?.name || "Unknown Physician"}
                                  </p>
                                  {thread.unread_count > 0 && (
                                    <Badge className="bg-red-500 text-xs">{thread.unread_count}</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-300 truncate">{thread.subject}</p>
                                {thread.patient_name && (
                                  <p className="text-xs text-slate-500 mt-1">Re: {thread.patient_name}</p>
                                )}
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className={PRIORITY_CONFIG[thread.priority]?.color || "bg-blue-500"} variant="secondary">
                                    {thread.priority}
                                  </Badge>
                                  <span className="text-xs text-slate-500">
                                    {thread.last_message_at ? new Date(thread.last_message_at).toLocaleDateString() : ""}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
                
                <TabsContent value="archived" className="m-0">
                  <ScrollArea className="h-[500px]">
                    {threads.length === 0 ? (
                      <div className="text-center py-12 text-slate-400">
                        <Archive className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No archived messages</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-700">
                        {threads.map((thread) => (
                          <div
                            key={thread.id}
                            className="p-4 hover:bg-slate-700/50 cursor-pointer"
                            onClick={() => loadThreadMessages(thread.id)}
                          >
                            <p className="font-medium text-white truncate">{thread.subject}</p>
                            <p className="text-sm text-slate-400 truncate mt-1">
                              {thread.participants?.find(p => p.type === "external")?.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Message View */}
          <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
            {selectedThread ? (
              <>
                <CardHeader className="border-b border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white">{selectedThread.subject}</CardTitle>
                      <CardDescription className="text-slate-400">
                        {selectedThread.participants?.map(p => p.name).join(", ")}
                        {selectedThread.patient_name && ` • Patient: ${selectedThread.patient_name}`}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={PRIORITY_CONFIG[selectedThread.priority]?.color}>{selectedThread.priority}</Badge>
                      <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handleArchiveThread(selectedThread.id)}>
                        <Archive className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0 flex flex-col h-[500px]">
                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg, idx) => (
                        <div
                          key={msg.id || idx}
                          className={`flex ${msg.sender_type === "internal" ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`max-w-[80%] rounded-lg p-3 ${
                            msg.sender_type === "internal" 
                              ? "bg-indigo-600 text-white" 
                              : "bg-slate-700 text-white"
                          }`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{msg.sender_name}</span>
                              <span className="text-xs opacity-75">
                                {new Date(msg.sent_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  {/* Reply Box */}
                  <div className="p-4 border-t border-slate-700">
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your secure message..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white flex-1 min-h-[80px]"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) handleSendReply();
                        }}
                        data-testid="reply-input"
                      />
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs text-slate-500">
                        <Shield className="h-3 w-3 inline mr-1" />
                        End-to-end encrypted • HIPAA compliant
                      </p>
                      <Button onClick={handleSendReply} disabled={!replyMessage.trim() || sending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-reply-btn">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        Send (Ctrl+Enter)
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </>
            ) : (
              <CardContent className="flex flex-col items-center justify-center h-[600px] text-slate-400">
                <MessageSquare className="h-16 w-16 mb-4 opacity-50" />
                <p className="text-lg mb-2">Select a conversation</p>
                <p className="text-sm">or start a new secure message</p>
                <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowNewThread(true)}>
                  <Plus className="h-4 w-4 mr-2" />New Message
                </Button>
              </CardContent>
            )}
          </Card>
        </div>

        {/* Contacts Section */}
        <Card className="bg-slate-800 border-slate-700 mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-indigo-400" />
                Physician Contacts
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search contacts..."
                    value={searchContacts}
                    onChange={(e) => { setSearchContacts(e.target.value); loadContacts(); }}
                    className="pl-10 bg-slate-700 border-slate-600 text-white w-64"
                  />
                </div>
                <Button onClick={() => setShowNewContact(true)} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="h-4 w-4 mr-2" />Add Contact
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contacts.map((contact) => (
                <div key={contact.id} className="p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-12 w-12 bg-indigo-500/20">
                      <AvatarFallback className="text-indigo-300">{getInitials(contact.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white">{contact.name}</p>
                      {contact.specialty && <p className="text-sm text-slate-400">{contact.specialty}</p>}
                      {contact.organization && <p className="text-sm text-slate-500">{contact.organization}</p>}
                      {contact.npi && <p className="text-xs text-slate-500">NPI: {contact.npi}</p>}
                    </div>
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => {
                        setNewThread({ ...newThread, recipient_physician_id: contact.id, recipient_name: contact.name, recipient_email: contact.email });
                        setShowNewThread(true);
                      }}
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {contacts.length === 0 && (
                <div className="col-span-full text-center py-8 text-slate-400">
                  <User className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No contacts found</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Thread Dialog */}
      <Dialog open={showNewThread} onOpenChange={setShowNewThread}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>New Secure Message</DialogTitle>
            <DialogDescription className="text-slate-400">Send an encrypted message to a referring physician</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Recipient Physician</Label>
              <Select
                value={newThread.recipient_physician_id}
                onValueChange={(v) => {
                  const contact = contacts.find(c => c.id === v);
                  setNewThread({ ...newThread, recipient_physician_id: v, recipient_name: contact?.name || "", recipient_email: contact?.email || "" });
                }}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Select from contacts..." />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} {c.organization ? `(${c.organization})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="text-center text-slate-500 text-sm">or enter manually</div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Physician Name</Label>
                <Input
                  value={newThread.recipient_name}
                  onChange={(e) => setNewThread({ ...newThread, recipient_name: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                  placeholder="Dr. John Smith"
                />
              </div>
              <div>
                <Label>Email (optional)</Label>
                <Input
                  type="email"
                  value={newThread.recipient_email}
                  onChange={(e) => setNewThread({ ...newThread, recipient_email: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                  placeholder="doctor@hospital.com"
                />
              </div>
            </div>

            <div>
              <Label>Subject *</Label>
              <Input
                value={newThread.subject}
                onChange={(e) => setNewThread({ ...newThread, subject: e.target.value })}
                className="bg-slate-700 border-slate-600"
                placeholder="RE: Patient Referral"
                data-testid="new-thread-subject"
              />
            </div>

            <div>
              <Label>Patient Name (optional)</Label>
              <Input
                value={newThread.patient_name}
                onChange={(e) => setNewThread({ ...newThread, patient_name: e.target.value })}
                className="bg-slate-700 border-slate-600"
                placeholder="For context..."
              />
            </div>

            <div>
              <Label>Priority</Label>
              <Select value={newThread.priority} onValueChange={(v) => setNewThread({ ...newThread, priority: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Message *</Label>
              <Textarea
                value={newThread.message}
                onChange={(e) => setNewThread({ ...newThread, message: e.target.value })}
                className="bg-slate-700 border-slate-600 min-h-[120px]"
                placeholder="Your secure message..."
                data-testid="new-thread-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-600" onClick={() => setShowNewThread(false)}>Cancel</Button>
            <Button onClick={handleCreateThread} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-thread-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Contact Dialog */}
      <Dialog open={showNewContact} onOpenChange={setShowNewContact}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add Physician Contact</DialogTitle>
            <DialogDescription className="text-slate-400">Add a new referring physician to your contacts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Physician Name *</Label>
              <Input
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                className="bg-slate-700 border-slate-600"
                placeholder="Dr. Jane Smith"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>NPI Number</Label>
                <Input
                  value={newContact.npi}
                  onChange={(e) => setNewContact({ ...newContact, npi: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                  placeholder="1234567890"
                />
              </div>
              <div>
                <Label>Specialty</Label>
                <Input
                  value={newContact.specialty}
                  onChange={(e) => setNewContact({ ...newContact, specialty: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                  placeholder="Oncology"
                />
              </div>
            </div>
            <div>
              <Label>Organization</Label>
              <Input
                value={newContact.organization}
                onChange={(e) => setNewContact({ ...newContact, organization: e.target.value })}
                className="bg-slate-700 border-slate-600"
                placeholder="Virginia Hospital"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-600" onClick={() => setShowNewContact(false)}>Cancel</Button>
            <Button onClick={handleAddContact} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-2" />Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
